import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MomentosWowService } from '../../../../services/momentos-wow/momentos-wow.service';
import { MatSort } from '@angular/material/sort';

type CategoryKey = 'cumpleanos' | 'quinquenios' | 'nacimientos';

interface BaseRow {
  id: number;
  nombreCompleto: string;
  fechaAntiguedad: string;
  celular: string;
  correoElectronico: string;
  category: CategoryKey;
}

interface CumpleanosRow extends BaseRow {
  category: 'cumpleanos';
  parentesco: string;
  fechaCumpleanosConEdad: string;
}

interface QuinqueniosRow extends BaseRow {
  category: 'quinquenios';
  quinquenios: number;
}

interface NacimientosRow extends BaseRow {
  category: 'nacimientos';
  fechaIngreso: string;
}

type TableRow = CumpleanosRow | QuinqueniosRow | NacimientosRow;

interface AuditLogEntry {
  usuario: string;
  fechaHora: string;
  evento: string;
  categoria: string;
}

@Component({
  selector: 'app-dialog-momentos-wow',
  templateUrl: './dialog-momentos-wow.component.html',
  styleUrls: ['./dialog-momentos-wow.component.css'],
})
export class DialogMomentosWowComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  readonly pageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];

  selectedCategory: CategoryKey = 'cumpleanos';
  displayedColumns: string[] = [];
  dataSource = new MatTableDataSource<TableRow>([]);
  auditLog: AuditLogEntry[] = [];
  @ViewChild(MatSort) sort!: MatSort;

  private allRowsByCategory: Record<CategoryKey, TableRow[]> = {
    cumpleanos: [],
    quinquenios: [],
    nacimientos: [],
  };

  constructor(
    public dialogRef: MatDialogRef<DialogMomentosWowComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private momentosWowService: MomentosWowService,
  ) {}

  ngOnInit(): void {
    console.log('DATA RECIBIDA EN DIALOG:', this.data);
    this.buildRows();
    console.log('ROWS PROCESADAS:', this.allRowsByCategory);
    this.selectCategory('cumpleanos');
  }

  ngAfterViewInit(): void {
    this.configurarOrdenamiento();
  }

  private configurarOrdenamiento(): void {
    this.dataSource.sortingDataAccessor = (
      row: any,
      columna: string,
    ): string | number => {
      const valor = row[columna];

      switch (columna) {
        case 'fechaAntiguedad':
        case 'fechaCumpleanosConEdad':
        case 'fechaIngreso':
          return this.convertirFechaParaOrdenar(valor);

        case 'quinquenios':
        case 'celular':
          return Number(valor) || 0;

        case 'nombreCompleto':
        case 'parentesco':
        case 'correoElectronico':
          return String(valor ?? '')
            .trim()
            .toLocaleLowerCase('es-CO');

        default:
          return String(valor ?? '')
            .trim()
            .toLocaleLowerCase('es-CO');
      }
    };

    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  private convertirFechaParaOrdenar(valor: unknown): number {
    if (!valor || valor === '-') {
      return 0;
    }

    const texto = String(valor).trim();

    const match = texto.match(/^(\d{2})\/(\d{2})\/(\d{4})/);

    if (match) {
      const [, dia, mes, anio] = match;

      return new Date(
        Number(anio),
        Number(mes) - 1,
        Number(dia),
      ).getTime();
    }

    const fecha = this.parseDate(texto);

    return fecha ? fecha.getTime() : 0;
  }

  close(): void {
    this.dialogRef.close();
  }

  selectCategory(category: CategoryKey): void {
    this.selectedCategory = category;
    this.displayedColumns = this.getColumnsForCategory(category);

    const datos = this.allRowsByCategory[category] ?? [];

    this.dataSource.data = [...datos];

    if (this.paginator) {
      this.paginator.firstPage();
    }

    if (this.sort) {
      this.sort.active = '';
      this.sort.direction = '';
      this.configurarOrdenamiento();
    }
  }

  getCount(category: CategoryKey): number {
    return this.allRowsByCategory[category].length;
  }

  markAsManaged(row: TableRow): void {
    this.momentosWowService.gestionar(row.id).subscribe({
      next: () => {
        const category = this.selectedCategory;

        this.allRowsByCategory[category] = this.allRowsByCategory[
          category
        ].filter((item) => item.id !== row.id);

        this.dataSource.data = [...this.allRowsByCategory[category]];

        // 3. ajustar paginador
        if (this.paginator) {
          const maxPageIndex = Math.max(
            Math.ceil(this.dataSource.data.length / this.paginator.pageSize) -
              1,
            0,
          );

          if (this.paginator.pageIndex > maxPageIndex) {
            this.paginator.pageIndex = maxPageIndex;
          }
        }

        // 4. auditoría
        this.registerAuditEvent(row);
      },

      error: (err) => {
        console.error('Error gestionando momento WOW', err);
      },
    });
  }

  private registerAuditEvent(row: TableRow): void {
    const user = this.getCurrentUser();
    const entry: AuditLogEntry = {
      usuario: user,
      fechaHora: new Date().toISOString(),
      evento: row.nombreCompleto,
      categoria: this.getCategoryLabel(this.selectedCategory),
    };

    this.auditLog.push(entry);
    // Registro temporal de bitacora en cliente.
    console.log('Bitacora Momentos WOW:', entry);
  }

  private getCurrentUser(): string {
    const raw = sessionStorage.getItem('user');
    if (!raw) {
      return 'Usuario no identificado';
    }

    try {
      const user = JSON.parse(raw);
      return (
        user?.name ||
        user?.document ||
        user?.username ||
        'Usuario no identificado'
      );
    } catch {
      return 'Usuario no identificado';
    }
  }

  private buildRows(): void {
    const payload = this.data || {};

    this.allRowsByCategory = {
      cumpleanos: this.mapCumpleanos(payload.cumpleanos || []),
      quinquenios: this.mapQuinquenios(payload.quinquenios || []),
      nacimientos: this.mapNacimientos(payload.nacimientos || []),
    };
  }

  private mapCumpleanos(rawRows: any[]): CumpleanosRow[] {
    return rawRows
      .map((row) => {
        if (
          row.tipoMomento !== 'CUMPLEAÑOS' ||
          row.visible !== '1' ||
          row.gestionado !== '0'
        ) {
          return null;
        }

        const fechaWow = this.parseDate(row.fechaCumpleanos);

        console.log('Procesando fila de cumpleaños:', row, 'Fecha WOW parseada:', fechaWow);

        if (!fechaWow) {
          return null;
        }

        return {
          id: row.id,
          category: 'cumpleanos' as const, 
          nombreCompleto: row.nombreCompleto || '-',
          fechaAntiguedad: this.formatDate(row.fechaAntiguedad),
          parentesco: row.parentesco || '-',
          fechaCumpleanosConEdad: row.edadTexto || '-',
          celular: row.celular || '-',
          correoElectronico: row.correo || '-',
          fechaOrden: fechaWow.getTime(),
        };
      })
      .filter((row): row is CumpleanosRow & { fechaOrden: number } => row !== null)
      .sort((a, b) => b.fechaOrden - a.fechaOrden)
      .map(({ fechaOrden, ...row }) => row);
  }

  private mapQuinquenios(rawRows: any[]): QuinqueniosRow[] {
    return rawRows
      .map((row) => {
        if (
          row.tipoMomento !== 'QUINQUENIO' ||
          row.visible !== '1' ||
          row.gestionado !== '0'
        ) {
          return null;
        }

        const fechaWow = this.parseDate(row.fechaAntiguedad);

        console.log('Procesando fila de quinquenios:', row, 'Fecha WOW parseada:', fechaWow);

        if (!fechaWow) {
          return null;
        }

        return {
          id: row.id,
          category: 'quinquenios' as const,
          nombreCompleto: row.nombreCompleto || '-',
          fechaAntiguedad: this.formatDate(row.fechaAntiguedad),
          quinquenios: row.quinquenio,
          celular: row.celular || '-',
          correoElectronico: row.correo || '-',
          fechaOrden: fechaWow.getTime(),
        };
      })
      .filter((row): row is QuinqueniosRow & { fechaOrden: number } => row !== null)
      .sort((a, b) => b.fechaOrden - a.fechaOrden)
      .map(({ fechaOrden, ...row }) => row);
  }

  private mapNacimientos(rawRows: any[]): NacimientosRow[] {
    return rawRows
      .map((row) => {
        if (
          row.tipoMomento !== 'NACIMIENTO' ||
          row.visible !== '1' ||
          row.gestionado !== '0'
        ) {
          return null;
        }

        const fechaWow = this.parseDate(row.fechaIngreso);

        console.log('Procesando fila de nacimientos:', row, 'Fecha WOW parseada:', fechaWow);

        if (!fechaWow) {
          return null;
        }

        return {
          id: row.id,
          category: 'nacimientos' as const,
          nombreCompleto: row.nombreCompleto || '-',
          fechaAntiguedad: this.formatDate(row.fechaAntiguedad),
          fechaIngreso: this.formatDate(row.fechaIngreso),
          celular: row.celular || '-',
          correoElectronico: row.correo || '-',
          fechaOrden: fechaWow.getTime(),
        };
      })
      .filter((row): row is NacimientosRow & { fechaOrden: number } => row !== null)
      .sort((a, b) => b.fechaOrden - a.fechaOrden)
      .map(({ fechaOrden, ...row }) => row);
  }

  private getColumnsForCategory(category: CategoryKey): string[] {
    if (category === 'cumpleanos') {
      return [
        'nombreCompleto',
        'fechaAntiguedad',
        'parentesco',
        'fechaCumpleanosConEdad',
        'celular',
        'correoElectronico',
        'acciones',
      ];
    }

    if (category === 'quinquenios') {
      return [
        'nombreCompleto',
        'fechaAntiguedad',
        'quinquenios',
        'celular',
        'correoElectronico',
        'acciones',
      ];
    }

    return [
      'nombreCompleto',
      'fechaAntiguedad',
      'fechaIngreso',
      'celular',
      'correoElectronico',
      'acciones',
    ];
  }

  private getCategoryLabel(category: CategoryKey): string {
    if (category === 'cumpleanos') return 'Cumpleanos';
    if (category === 'quinquenios') return 'Quinquenios';
    return 'Nacimientos';
  }

  private parseDate(value: string | Date): Date | null {
    if (!value) return null;

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value;
    }

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      return new Date(y, m - 1, d);
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private formatDate(value: string | Date): string {
    const parsed = this.parseDate(value);
    if (!parsed) {
      return '-';
    }

    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();

    return `${day}/${month}/${year}`;
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
}
